import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Loading, ErrorBox, PageTitle } from '../components/admin/ui';

/**
 * "App do Cliente" — o hub da incorporadora sobre o próprio app do comprador.
 * Reúne o que antes vivia solto no menu (Obras, Boletos) e a marca do app, mais
 * o status do pacote, a contagem de acessos e a prévia "ver como meu cliente vê".
 * Obras e Boletos são as MESMAS telas, agora abertas aqui dentro.
 */

/** Redimensiona a imagem para um quadrado data URL (mesma lógica do logo na sidebar). */
function redimensionarParaDataUrl(file: File, size = 256): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('canvas indisponível'));
      const lado = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - lado) / 2, (img.height - lado) / 2, lado, lado, 0, 0, size, size);
      resolve(canvas.toDataURL('image/webp', 0.85));
    };
    img.onerror = () => reject(new Error('imagem inválida'));
    img.src = URL.createObjectURL(file);
  });
}

export function AppClientePage() {
  const { data, loading, error, reload } = useAsync(() => trpc.appCliente.info.query());

  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox msg={error ?? 'Não foi possível carregar.'} onRetry={reload} />;

  return (
    <div className="mx-auto max-w-3xl">
      <PageTitle titulo="App do Cliente" />
      <p className="-mt-4 mb-6 text-sm text-ink-3">
        O aplicativo que os seus compradores usam para acompanhar o contrato, a obra e os boletos.
      </p>

      {data.ativo ? (
        <div className="flex flex-col gap-5">
          <StatusPacote slug={data.slug} linkPublico={data.linkPublico} />
          <MarcaDoApp logoUrl={data.logoUrl} corMarca={data.corMarca} onSalvo={reload} />
          <div className="grid gap-5 sm:grid-cols-2">
            <CardLink href="/app-cliente/obras" titulo="Obras" descricao="O cronograma de cada empreendimento, etapa por etapa." />
            <CardLink href="/app-cliente/boletos" titulo="Boletos" descricao="A fila de pedidos de segunda via feitos no app." />
          </div>
          <Compradores acessos={data.acessos} />
          <Previa compradorExemploId={data.compradorExemploId} linkPublico={data.linkPublico} />
        </div>
      ) : (
        <PacoteInativo />
      )}
    </div>
  );
}

const secao = 'rounded-2xl border border-line bg-white p-5';
const rotulo = 'text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-4';

function StatusPacote({ slug, linkPublico }: { slug: string | null; linkPublico: string | null }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    if (!linkPublico) return;
    try {
      await navigator.clipboard.writeText(linkPublico);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      /* clipboard indisponível: o link segue visível para copiar à mão */
    }
  };
  return (
    <div className={secao}>
      <div className="flex items-center justify-between">
        <div className={rotulo}>Status</div>
        <span className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-semibold text-accent">Ativo</span>
      </div>
      <div className="mt-3 text-sm text-ink-2">
        Endereço do app (o comprador usa este link para acessar):
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto rounded-lg bg-bg-warm px-3 py-2 text-sm text-ink" data-testid="app-link">
          {linkPublico ?? 'endereço ainda não definido'}
        </code>
        <button
          type="button"
          onClick={copiar}
          disabled={!linkPublico}
          className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-2 hover:bg-bg-warm disabled:opacity-50"
        >
          {copiado ? 'Copiado' : 'Copiar link'}
        </button>
      </div>
      {slug && <div className="mt-2 text-xs text-ink-4">Identificador (slug): {slug}</div>}
    </div>
  );
}

function MarcaDoApp({
  logoUrl,
  corMarca,
  onSalvo,
}: {
  logoUrl: string | null;
  corMarca: string | null;
  onSalvo: () => void;
}) {
  const [cor, setCor] = useState(corMarca ?? '#005C3D');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => setCor(corMarca ?? '#005C3D'), [corMarca]);
  const hexValido = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(cor);

  const enviarLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const dataUrl = await redimensionarParaDataUrl(file);
      await trpc.salvarLogoIncorporadora.mutate({ logoUrl: dataUrl });
      onSalvo();
    } catch {
      setMsg('Não foi possível enviar a imagem. Use um PNG de pelo menos 192 pixels.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const salvarCor = async () => {
    if (!hexValido) return;
    setBusy(true);
    setMsg(null);
    try {
      await trpc.appCliente.setCorMarca.mutate({ cor });
      onSalvo();
      setMsg('Cor salva.');
    } catch {
      setMsg('Não foi possível salvar a cor.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={secao}>
      <div className={rotulo}>Marca do app</div>
      <p className="mt-2 text-sm text-ink-3">
        O logo e a cor aparecem para o comprador no aplicativo. Capriche: é a cara da sua incorporadora com o cliente.
      </p>

      <div className="mt-4 flex items-center gap-4">
        <div
          className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-line bg-bg-warm"
          aria-hidden={!logoUrl}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="Logo atual" className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs text-ink-4">sem logo</span>
          )}
        </div>
        <div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-2 hover:bg-bg-warm disabled:opacity-50"
          >
            {logoUrl ? 'Trocar logo' : 'Enviar logo'}
          </button>
          <div className="mt-1 text-xs text-ink-4">PNG de pelo menos 192 pixels.</div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            data-testid="app-logo-input"
            onChange={enviarLogo}
          />
        </div>
      </div>

      <div className="mt-5">
        <label className="text-sm font-medium text-ink-2">Cor de destaque</label>
        <div className="mt-2 flex items-center gap-3">
          <input
            type="color"
            value={hexValido ? cor : '#005C3D'}
            onChange={(e) => setCor(e.target.value)}
            className="h-10 w-12 cursor-pointer rounded-lg border border-line bg-white"
            aria-label="Escolher cor de destaque"
          />
          <input
            type="text"
            value={cor}
            onChange={(e) => setCor(e.target.value)}
            className="w-32 rounded-lg border border-line px-3 py-2 text-sm"
            data-testid="app-cor-input"
          />
          <button
            type="button"
            onClick={salvarCor}
            disabled={busy || !hexValido}
            className="rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Salvar cor
          </button>
        </div>
        {!hexValido && <div className="mt-1 text-xs text-red-600">Use uma cor no formato #RRGGBB.</div>}
      </div>

      {msg && <div className="mt-3 text-sm text-ink-3">{msg}</div>}
    </div>
  );
}

function CardLink({ href, titulo, descricao }: { href: string; titulo: string; descricao: string }) {
  return (
    <Link
      href={href}
      className="group flex flex-col rounded-2xl border border-line bg-white p-5 transition-colors hover:border-accent"
    >
      <div className="flex items-center justify-between">
        <span className="font-serif text-lg text-ink">{titulo}</span>
        <span className="text-accent transition-transform group-hover:translate-x-0.5" aria-hidden="true">
          {'→'}
        </span>
      </div>
      <span className="mt-1 text-sm text-ink-3">{descricao}</span>
    </Link>
  );
}

function Compradores({ acessos }: { acessos: number }) {
  return (
    <div className={secao}>
      <div className={rotulo}>Compradores</div>
      {acessos > 0 ? (
        <div className="mt-2 text-sm text-ink-2">
          <span className="font-serif text-2xl text-ink">{acessos}</span>{' '}
          {acessos === 1 ? 'acesso pelo link até agora.' : 'acessos pelo link até agora.'}
        </div>
      ) : (
        <div className="mt-2 text-sm text-ink-3">Nenhum acesso ainda.</div>
      )}
    </div>
  );
}

function Previa({
  compradorExemploId,
  linkPublico,
}: {
  compradorExemploId: string | null;
  linkPublico: string | null;
}) {
  const [, setLocation] = useLocation();
  return (
    <div className={secao}>
      <div className={rotulo}>Ver como meu cliente vê</div>
      <p className="mt-2 text-sm text-ink-3">
        Abra uma prévia do aplicativo com os dados reais de um contrato da sua carteira, do jeito que o comprador enxerga.
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={!compradorExemploId}
          onClick={() => setLocation('/app-cliente/previa')}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          data-testid="previa-botao"
        >
          Ver como meu cliente vê
        </button>
        {linkPublico && (
          <a
            href={linkPublico}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink-2 hover:bg-bg-warm"
          >
            Abrir a tela de acesso
          </a>
        )}
      </div>
      {!compradorExemploId && (
        <div className="mt-2 text-xs text-ink-4">
          A prévia com dados reais aparece assim que você tiver ao menos um contrato importado.
        </div>
      )}
    </div>
  );
}

function PacoteInativo() {
  return (
    <div className="rounded-2xl border border-line bg-white p-6">
      <div className="font-serif text-xl text-ink">O app do comprador ainda não está ativo</div>
      <p className="mt-2 text-sm text-ink-2">
        Com o app, cada comprador acompanha o próprio contrato, o andamento da obra e pede a segunda via do boleto,
        com a sua marca. Menos ligação para o seu time e mais tranquilidade para o cliente.
      </p>
      <p className="mt-4 text-sm text-ink-3">
        Para ativar na sua conta, fale com o suporte da AMA Capital.
      </p>
    </div>
  );
}
